import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { AdminHeader } from "../components/admin/AdminHeader";
import { Loader2, DollarSign, CalendarClock, TrendingUp, Hash } from "lucide-react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Cell,
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

const StatCard = ({ icon: Icon, label, value, accent, testid }) => (
  <div data-testid={testid} className="bg-sand-dark rounded-3xl p-6 border border-charcoal/5">
    <div className={`inline-flex p-2.5 rounded-xl ${accent}`}>
      <Icon size={20} strokeWidth={1.5} />
    </div>
    <p className="mt-4 font-heading text-4xl text-charcoal">{value}</p>
    <p className="mt-1 text-sm text-stone">{label}</p>
  </div>
);

export default function AdminFinance() {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => {
    axios
      .get(`${API}/finance/summary`, { headers: authHeaders })
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-sand">
      <div className="grain-overlay" />
      <div className="relative z-10">
        <AdminHeader active="finance" />
        <main className="max-w-6xl mx-auto px-6 py-10">
          <h1 className="font-heading text-4xl font-light text-charcoal mb-8">Finance overview</h1>

          {loading ? (
            <div className="flex justify-center py-32 text-stone"><Loader2 className="animate-spin" /></div>
          ) : !data ? (
            <p className="text-stone">Could not load finance data.</p>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                <StatCard testid="stat-earned" icon={DollarSign} label="Total earned (completed)" value={money(data.total_earned)} accent="bg-clay/15 text-clay" />
                <StatCard testid="stat-upcoming" icon={CalendarClock} label="Upcoming (confirmed)" value={money(data.upcoming)} accent="bg-moss/15 text-moss" />
                <StatCard testid="stat-avg" icon={TrendingUp} label="Avg. per session" value={money(data.avg_session)} accent="bg-clay/15 text-clay" />
                <StatCard testid="stat-count" icon={Hash} label="Completed sessions" value={data.completed_count} accent="bg-charcoal/10 text-charcoal" />
              </div>

              <div className="mt-8 bg-sand-dark rounded-3xl p-6 md:p-8 border border-charcoal/5" data-testid="finance-chart">
                <h2 className="font-heading text-2xl text-charcoal mb-6">Earnings — last 6 months</h2>
                <div style={{ width: "100%", height: 320 }}>
                  <ResponsiveContainer>
                    <BarChart data={data.monthly} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2DDD3" vertical={false} />
                      <XAxis dataKey="label" tick={{ fill: "#68645E", fontSize: 12 }} axisLine={{ stroke: "#E2DDD3" }} tickLine={false} />
                      <YAxis tick={{ fill: "#68645E", fontSize: 12 }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${v}`} />
                      <Tooltip
                        cursor={{ fill: "rgba(166,110,87,0.08)" }}
                        formatter={(v, n, p) => [money(v), `${p.payload.count} session${p.payload.count === 1 ? "" : "s"}`]}
                        contentStyle={{ borderRadius: 12, border: "1px solid #E2DDD3", background: "#F7F5F0" }}
                      />
                      <Bar dataKey="earned" radius={[8, 8, 0, 0]} maxBarSize={56}>
                        {data.monthly.map((_, i) => (
                          <Cell key={i} fill="#A66E57" />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <p className="mt-6 text-sm text-stone">
                Totals are based on the fee recorded on each booking. Set the fee when you confirm an
                appointment or add a manual booking. "Earned" counts completed sessions; "Upcoming" counts confirmed ones.
              </p>
            </>
          )}
        </main>
      </div>
    </div>
  );
}
